import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { isAdmin } from '../config/admins';
import ArchVisualizer from '../components/ArchVisualizer';

/**
 * ARCHX AI — Live Engine Monitor (admin only)
 * ------------------------------------------------------------------
 * Real ops view of the engine. Subscribes live to the `engine_status`
 * Firestore collection (written by the engine at every pipeline stage),
 * so it shows TRUE, durable case state that survives a Cloud Run recycle —
 * unlike the in-memory /status endpoint.
 *
 * States: queued · processing · review · done · failed (incl. "incomplete uploads").
 */

const STAGE_LABELS = {
  pending:        'Queued',
  ingesting:      'Ingesting scans',
  detecting:      'Detecting markers',
  registering:    'Registering scans',
  designing:      'Designing teeth',
  awaiting_review:'Awaiting review',
  done:           'Complete',
  error:          'Failed',
};

const STATE_STYLE = {
  queued:     { label: 'QUEUED',     color: '#93c5fd', glow: '#3b82f6' },
  processing: { label: 'PROCESSING', color: '#fbbf24', glow: '#f59e0b' },
  review:     { label: 'IN REVIEW',  color: '#c4b5fd', glow: '#8b5cf6' },
  done:       { label: 'DONE',       color: '#4ade80', glow: '#22c55e' },
  failed:     { label: 'FAILED',     color: '#f87171', glow: '#ef4444' },
};

function isIncomplete(c) {
  const e = (c.error || '').toLowerCase();
  return c.state === 'failed' && (e.includes('missing required') || e.includes('incomplete'));
}

function fmtTime(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function EngineMonitor({ user }) {
  const [cases, setCases] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, 'engine_status'), orderBy('updated_at', 'desc'));
    const unsub = onSnapshot(
      q,
      snap => { setCases(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoaded(true); },
      e => { setErr(e.message); setLoaded(true); }
    );
    return unsub;
  }, []);

  if (!isAdmin(user)) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>Admin only</div>
          <div style={{ color: '#94a3b8', marginBottom: '20px' }}>This view is restricted to ARCHX administrators.</div>
          <button onClick={() => navigate('/dashboard')} style={btn}>Back to dashboard</button>
        </div>
      </div>
    );
  }

  const counts = {
    processing: cases.filter(c => c.state === 'processing' || c.state === 'queued').length,
    failed:     cases.filter(c => c.state === 'failed').length,
    done:       cases.filter(c => c.state === 'done').length,
  };

  // The case to feature in the live arch hero: prefer an actively processing one.
  const active = cases.find(c => c.state === 'processing')
    || cases.find(c => c.state === 'queued')
    || cases[0];
  const activeStyle = active ? (STATE_STYLE[active.state] || STATE_STYLE.queued) : null;

  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(1200px 600px at 50% -10%, #14213d 0%, #0b1120 60%)', color: '#e2e8f0', padding: '0 0 60px' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 28px', borderBottom: '1px solid #1e293b' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 10px #22c55e', animation: 'emPulse 1.5s infinite' }} />
          <span style={{ fontWeight: 800, letterSpacing: '1px' }}>ARCHX AI · ENGINE MONITOR</span>
          <span style={{ fontSize: '11px', color: '#22c55e', fontWeight: 700, letterSpacing: '1px' }}>LIVE</span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={() => navigate('/dashboard')} style={btnGhost}>Dashboard</button>
          <button onClick={() => signOut(auth)} style={btnGhost}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: '980px', margin: '0 auto', padding: '28px 20px 0' }}>
        {/* Live arch hero — featured case building in real time */}
        {active && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(11,17,32,.96), rgba(20,33,61,.6))',
            border: `1px solid ${activeStyle.glow}33`, borderRadius: '18px',
            padding: '16px 18px 8px', marginBottom: '22px', overflow: 'hidden',
            boxShadow: active.state === 'processing' ? `0 0 30px ${activeStyle.glow}22` : 'none',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: 800, color: '#fbbf24' }}>{active.case_id || active.id}</span>
                <span style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1px', color: activeStyle.color, padding: '3px 10px', borderRadius: '20px', background: `${activeStyle.glow}1f` }}>
                  {(isIncomplete(active) ? 'INCOMPLETE' : activeStyle.label)}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>{STAGE_LABELS[active.stage] || active.stage}</div>
                <div style={{ fontFamily: 'monospace', fontSize: '22px', fontWeight: 800, color: '#e2e8f0' }}>{active.progress || 0}%</div>
              </div>
            </div>
            <ArchVisualizer stage={active.stage} progress={active.progress || 0} state={active.state} markers={active.markers} />
            {/* Real engine metrics, shown once reported */}
            {(active.markers != null || active.rmse != null) && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '22px', fontSize: '12px', color: '#94a3b8', marginTop: '-2px', marginBottom: '4px' }}>
                {active.markers != null && <span>Implant sites: <strong style={{ color: '#fbbf24', fontFamily: 'monospace' }}>{active.markers}</strong></span>}
                {active.rmse != null && <span>Registration RMSE: <strong style={{ color: active.rmse <= 0.3 ? '#4ade80' : '#fbbf24', fontFamily: 'monospace' }}>{active.rmse} mm</strong></span>}
              </div>
            )}
            <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', paddingBottom: '6px' }}>{active.message}</div>
          </div>
        )}

        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '24px' }}>
          <Stat n={counts.processing} label="ACTIVE / QUEUED" color="#fbbf24" />
          <Stat n={counts.failed} label="FAILED" color="#f87171" />
          <Stat n={counts.done} label="COMPLETED" color="#4ade80" />
        </div>

        {err && (
          <div style={{ padding: '14px 18px', borderRadius: '10px', background: '#3b1d1d', color: '#fca5a5', marginBottom: '20px', fontSize: '13px' }}>
            Couldn't read engine status: {err}. (Check Firestore rules allow admin read of <code>engine_status</code>.)
          </div>
        )}

        {!loaded ? (
          <div style={{ color: '#64748b', textAlign: 'center', padding: '60px' }}>Connecting to the engine…</div>
        ) : cases.length === 0 ? (
          <div style={{ color: '#64748b', textAlign: 'center', padding: '60px', border: '1px dashed #1e293b', borderRadius: '14px' }}>
            No cases yet. When a case is submitted, it appears here in real time.
          </div>
        ) : (
          cases.map(c => <CaseCard key={c.id} c={c} />)
        )}
      </div>

      <style>{`
        @keyframes emPulse {0%,100%{opacity:1}50%{opacity:.3}}
        @keyframes emBar {0%{background-position:0 0}100%{background-position:40px 0}}
      `}</style>
    </div>
  );
}

function CaseCard({ c }) {
  const st = STATE_STYLE[c.state] || STATE_STYLE.queued;
  const incomplete = isIncomplete(c);
  const stageLabel = STAGE_LABELS[c.stage] || c.stage || '—';
  const pct = Math.max(0, Math.min(100, c.progress || 0));
  const active = c.state === 'processing';

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(15,23,42,.9), rgba(30,41,59,.7))',
      border: `1px solid ${st.glow}33`, borderLeft: `3px solid ${st.glow}`,
      borderRadius: '14px', padding: '18px 20px', marginBottom: '14px',
      boxShadow: active ? `0 0 24px ${st.glow}22` : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: 700, color: '#fbbf24' }}>{c.case_id || c.id}</span>
          <span style={{
            fontSize: '11px', fontWeight: 800, letterSpacing: '1px',
            color: st.color, padding: '3px 10px', borderRadius: '20px',
            background: `${st.glow}1f`, display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}>
            {active && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: st.glow, boxShadow: `0 0 8px ${st.glow}`, animation: 'emPulse 1.2s infinite' }} />}
            {incomplete ? 'INCOMPLETE' : st.label}
          </span>
        </div>
        <span style={{ fontSize: '11px', color: '#64748b' }}>{fmtTime(c.updated_at)}</span>
      </div>

      {/* Progress */}
      <div style={{ margin: '14px 0 8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
          <span style={{ color: '#cbd5e1' }}>{stageLabel}</span>
          <span style={{ fontFamily: 'monospace', color: st.color }}>{pct}%</span>
        </div>
        <div style={{ height: '7px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${pct}%`, borderRadius: '4px',
            background: c.state === 'failed'
              ? '#ef4444'
              : `repeating-linear-gradient(90deg, ${st.glow}, ${st.glow} 12px, ${st.glow}cc 12px, ${st.glow}cc 20px)`,
            backgroundSize: '40px 100%',
            animation: active ? 'emBar 0.8s linear infinite' : 'none',
            transition: 'width .4s ease',
          }} />
        </div>
      </div>

      {/* Message + metrics */}
      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>{c.message}</div>

      <div style={{ display: 'flex', gap: '20px', marginTop: '10px', flexWrap: 'wrap', fontSize: '12px' }}>
        {c.rmse != null && <Meta label="Registration RMSE" value={`${c.rmse} mm`} good={c.rmse <= 0.3} />}
        {c.design_ready && <Meta label="Design" value="ready ✓" good />}
        {c.clinician_id && <Meta label="Clinician" value={c.clinician_id} />}
      </div>

      {/* Failure / incomplete reason */}
      {c.error && (
        <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '8px', background: '#3b1d1d', color: '#fca5a5', fontSize: '12px' }}>
          {incomplete ? '⛔ Incomplete upload — ' : '⚠️ '} {c.error}
        </div>
      )}

      {/* Warnings */}
      {Array.isArray(c.warnings) && c.warnings.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          {c.warnings.slice(0, 5).map((w, i) => (
            <div key={i} style={{ fontSize: '11px', color: '#fbbf24aa', marginTop: '3px' }}>• {w}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ n, label, color }) {
  return (
    <div style={{ background: 'rgba(15,23,42,.7)', border: '1px solid #1e293b', borderRadius: '14px', padding: '18px 20px' }}>
      <div style={{ fontSize: '30px', fontWeight: 800, fontFamily: 'monospace', color }}>{n}</div>
      <div style={{ fontSize: '11px', color: '#94a3b8', letterSpacing: '1px', marginTop: '2px' }}>{label}</div>
    </div>
  );
}

function Meta({ label, value, good }) {
  return (
    <span style={{ color: '#64748b' }}>
      {label}: <strong style={{ color: good ? '#4ade80' : '#e2e8f0', fontFamily: 'monospace' }}>{value}</strong>
    </span>
  );
}

const btn = { padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#c8922a', color: '#fff', fontWeight: 700, cursor: 'pointer' };
const btnGhost = { padding: '8px 16px', borderRadius: '8px', border: '1px solid #334155', background: 'transparent', color: '#cbd5e1', fontWeight: 600, fontSize: '13px', cursor: 'pointer' };
